import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useWebEDIStore } from 'stores/webedi/webedi';
import { DocumentTypeWithVirtual, PartnerV2 } from 'support/types';
import { Alert, AlertSection } from 'components/Display/Alert';
import { Popup } from 'components/Display/Popup/Popup';
import { Button } from 'components/Form/Button';
import { Translate } from 'components/Translate/Translate';
import { DocumentSummary } from 'components/WebEDI/DocumentSummary/DocumentSummary';
import { Summary } from 'components/WebEDI/DocumentSummary/types';

type SendConfirmationModalProps = {
  onConfirm: () => void;
  onCancel: () => void;
  open: boolean;
  documentType: DocumentTypeWithVirtual;
  receiverTradePartner: Pick<PartnerV2, 'name'> | undefined;
};
export const SendConfirmationModal: FC<SendConfirmationModalProps> = ({
  onConfirm,
  onCancel,
  open,
  documentType,
  receiverTradePartner,
}) => {
  const { t } = useTranslation();
  const documentTypeTranslation = t(`common:messageTypes.singular.${documentType}`);
  const isBig = useWebEDIStore((state) => state.isBigMessage);

  const CancelButton = () => (
    <Button analyticsId="webedi:cancelSendDialog" onClick={onCancel} variant="secondary">
      <Translate i18nKey="common:cancel" />
    </Button>
  );

  const PrimaryButton = () => (
    <Button analyticsId="webedi:confirmSendDialog" onClick={onConfirm}>
      {documentType === DocumentTypeWithVirtual.partialOrderConfirmation ? (
        <Translate i18nKey="webedi:sendDocumentConfirmation.partialOrderResponse.primaryButton" />
      ) : (
        <Translate
          i18nKey="webedi:sendDocumentConfirmation.primaryButton"
          values={{ documentType: documentTypeTranslation }}
        />
      )}
    </Button>
  );

  return (
    <Popup
      CancelButton={CancelButton}
      PrimaryButton={PrimaryButton}
      open={open}
      onClose={onCancel}
      title={
        documentType === DocumentTypeWithVirtual.partialOrderConfirmation ? (
          <Translate i18nKey="webedi:sendDocumentConfirmation.partialOrderResponse.title" />
        ) : (
          <Translate
            i18nKey="webedi:sendDocumentConfirmation.title"
            values={{ documentType: documentTypeTranslation }}
          />
        )
      }
      subtitle={
        <Translate
          i18nKey={`webedi:sendDocumentConfirmation.description.${documentType}`}
          values={{ documentType: documentTypeTranslation, receiverPartner: receiverTradePartner?.name }}
        />
      }
    >
      <DocumentSummary documentType={documentType} renderSummary={SummaryRenderer} />
      {isBig ? (
        <Alert severity="warning">
          <AlertSection severity="warning" title={t('webedi:sendDocumentConfirmation.bigMessage.title')}>
            <Translate i18nKey="webedi:sendDocumentConfirmation.bigMessage.description" />
          </AlertSection>
        </Alert>
      ) : null}
    </Popup>
  );
};

const SummaryRenderer = ({ summary }: { summary: Summary }) => {
  return (
    <div className="my-4 flex flex-col gap-4 rounded-md bg-gray-50 p-4">
      {summary.content.map((items) =>
        items
          .filter((item) => (item.test ? item.test('send-confirmation-modal') : true))
          .map((item) => (
            <p key={item.label} className="flex items-start justify-between gap-3 text-sm text-gray-500">
              <span className="shrink-0">{item.label}</span>
              <span className="text-right text-black">{item.value}</span>
            </p>
          )),
      )}
    </div>
  );
};

/**
 * i18n
 * t('webedi:sendDocumentConfirmation.description.ORDER')
 * t('webedi:sendDocumentConfirmation.description.ORDER_RESPONSE')
 * t('webedi:sendDocumentConfirmation.description.SHIPPING_NOTICE')
 * t('webedi:sendDocumentConfirmation.description.INVOICE')
 * t('webedi:sendDocumentConfirmation.description.CREDIT_NOTE')
 * t('webedi:sendDocumentConfirmation.description.PRODUCT_CATALOG')
 * t('webedi:sendDocumentConfirmation.description.PARTIAL_ORDER_CONFIRMATION')
 */
